import { decorate, inject, injectable } from 'inversify'
import { makeAutoObservable, runInAction } from 'mobx'
import { APIDataIntegrationEventLog } from '@evertel/types'
import { Api } from '@evertel/api'

class IntegrationEventLogSearchController {
    eventLogs: APIDataIntegrationEventLog[] = []
    count = 0
    page = 1
    limit = 25
    sortBy = 'createdDate'
    sortOrder: 'asc' | 'desc' = 'desc'
    isLoading = false
    integrationId = 0
    searchTerm = ''
    startDate: Date | null = null
    endDate: Date | null = null

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init(integrationId: number) {
        this.integrationId = integrationId
        this.reset()
    }

    setPage(page: number) {
        this.page = page
    }

    setLimit(limit: number) {
        this.limit = limit
        this.page = 1
    }

    setSorting(sortBy: string, sortOrder: 'asc' | 'desc') {
        this.sortBy = sortBy
        this.sortOrder = sortOrder
        this.page = 1
    }

    setSearchTerm(term: string) {
        this.searchTerm = term
        this.page = 1
    }

    setDateRange(start: Date | null, end: Date | null) {
        this.startDate = start
        this.endDate = end
        this.page = 1
    }

    search = async () => {
        if (!this.integrationId) return

        runInAction(() => {
            this.isLoading = true
        })

        try {
            const eventLogs = await this.api.Routes.Integration.getEventLogs(
                this.integrationId,
                this.startDate ?? undefined,
                this.endDate ?? undefined,
                this.searchTerm?.trim() || undefined,
                this.filter
            ) as APIDataIntegrationEventLog[]

            if (this.page === 1) {
                await this.fetchCount()
            }

            runInAction(() => {
                this.eventLogs = eventLogs || []
            })
        } catch (e) {
            console.error('IntegrationEventLogSearchController search()', e)
            throw e
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    fetchCount = async () => {
        if (!this.integrationId) return 0

        try {
            const result = await this.api.Routes.Integration.getEventLogsCount(
                this.integrationId,
                this.startDate ?? undefined,
                this.endDate ?? undefined,
                this.searchTerm?.trim() || undefined
            )

            runInAction(() => {
                this.count = result?.count ?? 0
            })
        } catch (e) {
            console.error('IntegrationEventLogSearchController fetchCount()', e)
        }

        return this.count
    }

    exportCsv = async () => {
        const result = await this.api.Routes.Integration.getEventLogsExport(
            this.integrationId,
            this.startDate ?? undefined,
            this.endDate ?? undefined,
            this.searchTerm?.trim() || undefined
        )
        return result
    }

    reset() {
        this.eventLogs = []
        this.count = 0
        this.page = 1
        this.sortBy = 'createdDate'
        this.sortOrder = 'desc'
        this.searchTerm = ''
        this.startDate = null
        this.endDate = null
    }

    get filter() {
        return {
            limit: this.limit,
            skip: (this.page - 1) * this.limit,
            order: `${this.sortBy} ${this.sortOrder}`
        }
    }
}

decorate(injectable(), IntegrationEventLogSearchController)
decorate(inject(Api), IntegrationEventLogSearchController, 0)

export { IntegrationEventLogSearchController }
